/**
 * The item identifier and description shown at the start of a line in cart, checkout, order,
 * open-order, quote and Easy Quote tables. The identifier is chosen by `renderCustomerPartNumber`
 * (`@ryerson/frontend.common`): `CPN: <partNumber>` when the line has a customer part number,
 * otherwise the `altPartNumber` when it starts with `CPN`, otherwise the FastPath number, and the
 * SKU (`materialMaster`) when none of the three is set. A line with only a non-`CPN`
 * `altPartNumber` shows an empty identifier.
 *
 * For an active product the identifier links to its product page (`/products/<id>`, with
 * `altPartNumber` as a query parameter, from `navigateToProductDetails`); for an inactive one it
 * is plain text; with no `itemDetail` it is an em dash. The description is `itemDetail.description`
 * or, failing that, the line's `itemDescription`.
 *
 * Used by `ShoppingCart/ShoppingCartTable/ShoppingCartTableFastPathCell.tsx`,
 * `Shared/CheckoutOrderDetails/CheckoutOrderDetailsTable/Helper/CheckoutOrderDetailsTableProductCell.tsx`,
 * `Checkout/OrderConfirmation/OrderConfirmationDetailsTable/OrderConfirmationDetailsCardViewItemHeader.tsx`,
 * `OrderDetails/Helpers/OrderItemDescription.tsx`,
 * `OrderDetails/Helpers/OrderProductStepperComponents/OrderDetailsCardItemHeader.tsx`,
 * `OrdersDashboard/Helpers/OpenOrders/OpenOrderTable/OpenOrderTableCardViewItemHeader.tsx`,
 * `QuoteDetails/Helpers/ViewProductsTableComponents/TableCellItemDescription.tsx`,
 * `QuoteDetails/Helpers/ViewProductsTableComponents/CardViewItemHeader.tsx` and
 * `EasyQuote/Helper/TableComponents/EasyQuoteTableProductCell.tsx`.
 */
import React from "react";
import { Link } from "@ryerson/frontend.navigation";
import { ITheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import {
	emDash,
	Language,
	LanguageToLocale,
	navigateToProductDetails,
	renderCustomerPartNumber,
} from "@ryerson/frontend.common";
import { Product } from "@ryerson/frontend.common";
import { ShoppingCartDataType } from "@ryerson/frontend.common";
import { expandedContentCSS, nonExpandedContentCSS } from "./Styles";
import { css } from "@emotion/react";

const fastPathItemDescriptionWrapper = (theme: ITheme) => css`
	@media only screen and (max-width: ${theme.breakpoints.lg}) {
		display: block;
		color: ${theme.colors.primary.white};
		margin-top: 15px;
	}
	word-wrap: break-word;
	white-space: normal;
`;

type ItemDetail = Pick<Product, "active" | "id" | "altPartNumber" | "fastPath"> & {
	description?: string;
};

type Item = Pick<ShoppingCartDataType, "partNumber" | "materialMaster" | "itemDescription">;

type FastPathLinkProps = {
	/** Product data for the line; absent renders an em dash instead of the identifier. */
	itemDetail?: ItemDetail;
	theme: ITheme;
	/** The row is expanded: white link with an underline border. */
	isExpanded: boolean;
	/** The line itself: customer part number, SKU and fallback description. */
	cartItem: Item;
	hideDescription?: boolean;
	/**Flag for use in checkout process: if so, display FastPath’s color as dark blue,
	 * instead of link blue */
	isCheckout?: boolean;
	isMobile?: boolean;
};

/** Item identifier (linked when the product is active) followed by the item description. */
export const FastPathLink = ({
	itemDetail,
	theme,
	isExpanded,
	cartItem,
	hideDescription,
	isCheckout,
	isMobile = false,
}: FastPathLinkProps) => {
	const { white, darkGray, secondaryBrand } = theme.colors.primary;
	const { lightBlue } = theme.colors.secondary;
	let linkColor: string;
	if (isExpanded || isMobile) {
		linkColor = white;
	} else {
		if (isCheckout) {
			linkColor = secondaryBrand;
		} else {
			linkColor = lightBlue;
		}
	}

	return (
		<Typography
			css={!isExpanded ? nonExpandedContentCSS : expandedContentCSS(theme)}
			size={isMobile ? "lg" : "sm"}
			color={isMobile ? white : undefined}
		>
			{!itemDetail ? (
				<>{emDash}</>
			) : itemDetail?.active ? (
				<Link
					type="secondary"
					color={linkColor}
					css={{
						...(isExpanded
							? {
									borderBottom: `1px solid ${white}`,
								}
							: {}),
						...(isMobile &&
							!isExpanded && {
								textDecoration: "underline",
								textDecorationColor: isMobile ? white : "",
							}),
					}}
					to={navigateToProductDetails(
						itemDetail?.id || "",
						itemDetail?.altPartNumber || ""
					)}
				>
					{renderCustomerPartNumber(
						itemDetail?.fastPath,
						cartItem?.partNumber,
						itemDetail?.altPartNumber || "",
						cartItem.materialMaster
					)}
				</Link>
			) : (
				<Typography color={isExpanded ? white : isMobile ? white : darkGray}>
					{renderCustomerPartNumber(
						itemDetail?.fastPath,
						cartItem?.partNumber,
						itemDetail?.altPartNumber || "",
						cartItem?.materialMaster
					)}
				</Typography>
			)}
			&nbsp;
			{isMobile ? (
				<Typography
					css={fastPathItemDescriptionWrapper(theme)}
					variant="span"
					line-height="sm"
					autoResize={true}
					size="md"
				>
					{!hideDescription && (itemDetail?.description || cartItem.itemDescription)}
				</Typography>
			) : (
				!hideDescription && (itemDetail?.description || cartItem.itemDescription)
			)}
		</Typography>
	);
};
